// COPIED from ptc-core web/src/feedback/feedbackContext.ts @ de39fff (fleet feedback contract v1, 2026-10-21).
// Changes from the source: `BUILD_ID` comes from this app's build stamp (`buildInfo.ts`), and
// `recentClientErrors` returns an empty list: this app has no client-error ring, and the contract makes
// it optional (§1). Building a capture path just for this would be a NEW data egress, which is the
// thing this module exists to be careful about. The `recentErrors` key is still always present (C4).
// v1.1 (2026-10-27, ptc-core #354): `uiLanguage` is added exactly as core adds it.
// Conformance: src/feedback/FeedbackWidgetConformance.test.tsx.

/**
 * The context a feedback report carries automatically (feedback-loop spec §3.2) — shown to the
 * reporter before it is sent, because telemetry a person cannot see erodes trust in exactly the
 * people this feature is for.
 *
 * ⚠ THIS IS A DATA EGRESS POINT. The server enforces it again (`FeedbackContentGuard`):
 *   - the ROUTE keeps its query string, because filter and view state live there and the developer
 *     must open exactly what the reporter saw — BUT free-text search parameters are stripped:
 *     `?q=John+Smith` is a client's name, not view state.
 */
import i18next from 'i18next';
import { BUILD_COMMIT, UNKNOWN } from '../buildInfo';

/** This build's commit, or `unknown` on an unstamped dev build. Never empty (C4). */
const BUILD_ID: string = BUILD_COMMIT ?? UNKNOWN;

/** This app keeps no client-error ring (see the banner), so there is nothing to attach. */
function recentClientErrors(): FeedbackRecentError[] {
  return [];
}

/** Query parameters that carry typed text rather than view state. */
export const FREE_TEXT_PARAMS = ['q', 'search', 'query', 'term'] as const;

export interface FeedbackRecentError {
  name: string;
  message: string;
  pathname: string;
  timestamp: string;
}

export interface FeedbackContext {
  route: string;
  buildSha: string;
  viewport: string;
  userAgent: string;
  clientTime: string;
  tz: string;
  recentErrors: FeedbackRecentError[];
  /** Contract v1.1 (2026-10-27): the UI language on screen, so a wrong-translation report names its language. */
  uiLanguage: string;
}

/** `pathname + search`, minus every free-text parameter. */
export function routeWithoutFreeText(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  for (const key of [...params.keys()]) {
    if ((FREE_TEXT_PARAMS as readonly string[]).includes(key.toLowerCase())) params.delete(key);
  }
  const rest = params.toString();
  return rest ? `${pathname}?${rest}` : pathname;
}

export function buildFeedbackContext(now: Date = new Date()): FeedbackContext {
  return {
    route: routeWithoutFreeText(window.location.pathname, window.location.search),
    buildSha: BUILD_ID,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    userAgent: navigator.userAgent,
    clientTime: now.toISOString(),
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'unknown',
    recentErrors: recentClientErrors().map(({ name, message, pathname, timestamp }) => ({
      name,
      message,
      pathname,
      timestamp,
    })),
    // The i18next singleton the header toggle drives: the language the staff member chose, not the browser's.
    uiLanguage: i18next.resolvedLanguage ?? i18next.language ?? 'en',
  };
}
